// Mockup Board API client — paths from docs/modules/mockup-board-api.md (Task 0 contract).
import { api } from '@/utils/api';
import {
  MOCKUP_ERRORS,
  type AnswerInput, type BoardTab, type BuildStatus, type MockupItem, type MockupListResponse, type MockupPin,
  type MockupPinListResponse, type PinInput, type ViewUrlResponse,
} from './types';

const P = '/mockup-board/mockups';

const decisionBody = <N>(notes: N, answers?: AnswerInput[], pinIds?: string[]) => ({
  notes,
  ...(answers ? { answers } : {}),
  ...(pinIds ? { pinIds } : {}),
});

export const mbApi = {
  list: async (tab: BoardTab): Promise<MockupListResponse> =>
    (await api.get(P, { params: { status: tab } })).data.data,
  get: async (id: string): Promise<MockupItem> =>
    (await api.get(`${P}/${id}`)).data.data,
  viewUrl: async (id: string): Promise<ViewUrlResponse> =>
    (await api.get(`${P}/${id}/view-url`)).data.data,
  // `answers` only when the mockup has questions, `pinIds` only when it has draft pins —
  // a mockup with neither keeps the old body { notes }.
  approve: async (id: string, notes: string | null, answers?: AnswerInput[], pinIds?: string[]): Promise<MockupItem> =>
    (await api.post(`${P}/${id}/approve`, decisionBody(notes, answers, pinIds))).data.data,
  reject: async (id: string, notes: string, answers?: AnswerInput[], pinIds?: string[]): Promise<MockupItem> =>
    (await api.post(`${P}/${id}/reject`, decisionBody(notes, answers, pinIds))).data.data,
  pins: async (id: string): Promise<MockupPinListResponse> =>
    (await api.get(`${P}/${id}/pins`)).data.data,
  addPin: async (id: string, input: PinInput): Promise<MockupPin> =>
    (await api.post(`${P}/${id}/pins`, input)).data.data,
  editPin: async (id: string, pinId: string, text: string): Promise<MockupPin> =>
    (await api.patch(`${P}/${id}/pins/${pinId}`, { text })).data.data,
  deletePin: async (id: string, pinId: string): Promise<{ id: string }> =>
    (await api.delete(`${P}/${id}/pins/${pinId}`)).data.data,
  addQuestions: async (id: string, questions: string[]): Promise<MockupItem> =>
    (await api.post(`${P}/${id}/questions`, { questions })).data.data,
  setBuildStatus: async (id: string, s: BuildStatus): Promise<MockupItem> =>
    (await api.patch(`${P}/${id}/build-status`, { buildStatus: s })).data.data,
  upload: async (f: { file: File; title: string; feature: string; note?: string; replacesId?: string; questions?: string[] }): Promise<MockupItem> => {
    const fd = new FormData();
    fd.append('title', f.title);
    fd.append('feature', f.feature);
    if (f.note) fd.append('note', f.note);
    if (f.replacesId) fd.append('replacesId', f.replacesId);
    if (f.questions?.length) fd.append('questions', JSON.stringify(f.questions));
    fd.append('file', f.file, f.file.name);
    return (await api.post(P, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data.data;
  },
  rerender: async (id: string): Promise<MockupItem> =>
    (await api.post(`${P}/${id}/rerender`)).data.data,
};

export const exportHref = (id: string, kind: 'html' | 'jpg') => `/api/v1/mockup-board/mockups/${id}/export.${kind}`;
export const thumbSrc = (id: string) => `/api/v1/mockup-board/mockups/${id}/thumb.jpg`;
/** Inline full-page JPEG for Comment mode (no export event, no attachment header). */
export const pictureSrc = (id: string) => `/api/v1/mockup-board/mockups/${id}/picture.jpg`;
/** Server-cut 480 × 270 crop around a pin, dot drawn. */
export const cropSrc = (id: string, pinId: string) => `/api/v1/mockup-board/mockups/${id}/pins/${pinId}/crop.jpg`;
export const errorText = (e: any): string => {
  const msg = e?.response?.data?.error;
  if (typeof msg === 'string' && msg) return msg;
  // nginx answers an oversize upload with its own HTML 413 page, not our JSON.
  if (e?.response?.status === 413) return MOCKUP_ERRORS.tooBig;
  return 'Something went wrong. Try again.';
};

export const fmtDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
